import { CountryCode, type ProjectCode } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { zodResolver } from '@hookform/resolvers/zod'
import { useApiECommerceUpdateOrderPaymentToken } from '@hooks-api/ec/use-api-ecommerce-orders-payment-token'
import { mutateAllECommerceOrders } from '@hooks-api/ec/useApiECommerceOrders'
import { useApiECommerceUpdateUserPaymentToken } from '@hooks-api/eCommerceV2/use-api-e-commerce-update-user-payment-token'
import { useApiECommerceUserPaymentTokens } from '@hooks-api/eCommerceV2/use-api-e-commerce-user-payment-tokens'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { HStack } from '@pets-ui/HStack/HStack'
import { Input } from '@pets-ui/Input/Input'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { filterCardholderName, filterDigitsOnly, filterEmail, getCountryCodeFromPathname } from '@utils'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isCardholderInfoComplete } from '../../utils/payment-token'
import { cardholderFormSchema, type CardholderFormValues } from './payment-method-card-holder-form-schema'
import { PaymentTokenItem } from './PaymentTokenItem'

export const DIALOG_EDIT_PAYMENT_METHOD = 'DIALOG_EDIT_PAYMENT_METHOD'

export interface DialogEditPaymentMethodProps {
  orderId: string
  fortuneOrderId: string
  paymentTokenId: number
  projectCode?: ProjectCode
  userId?: number
}

const DialogEditPaymentMethodContent = (props: DialogEditPaymentMethodProps) => {
  const { orderId, projectCode, userId, paymentTokenId, fortuneOrderId } = props
  const { t } = useTranslation()
  const { closeDialog } = useDialog(DIALOG_EDIT_PAYMENT_METHOD)
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const countryCode = getCountryCodeFromPathname(window.location.pathname, CountryCode.TW) as CountryCode
  const { destructuredData: paymentTokens, isLoading, mutate: mutateUserPaymentTokens } = useApiECommerceUserPaymentTokens({
    country_code: countryCode,
  })
  const { trigger: updatePaymentTokenInfo, isMutating: isPaymentTokenInfoUpdating } = useApiECommerceUpdateUserPaymentToken()
  const { trigger: updateOrderPaymentToken, isMutating: isOrderPaymentUpdating } = useApiECommerceUpdateOrderPaymentToken()
  const [selectedTokenId, setSelectedTokenId] = useState<number>(paymentTokenId)

  const selectedToken = paymentTokens?.find(token => token.payment_token_id === selectedTokenId)
  const isTapPay = countryCode === CountryCode.TW

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CardholderFormValues>({
    defaultValues: {
      cardholderNameEn: selectedToken?.cardholder?.name_en || '',
      email: selectedToken?.cardholder?.email || '',
      phoneCountryCode: selectedToken?.cardholder?.phone_number_country_code || '',
      phoneNumber: selectedToken?.cardholder?.phone_number || '',
    },
    resolver: zodResolver(cardholderFormSchema),
  })

  const handleSelectToken = (tokenId: number) => {
    setSelectedTokenId(tokenId)
    const selectedToken = paymentTokens?.find(token => token.payment_token_id === tokenId)
    // 處理更換 radio 選項時，重置驗證欄位初始值
    reset({
      cardholderNameEn: selectedToken?.cardholder?.name_en || '',
      email: selectedToken?.cardholder?.email || '',
      phoneCountryCode: selectedToken?.cardholder?.phone_number_country_code || '',
      phoneNumber: selectedToken?.cardholder?.phone_number || '',
    })
  }

  const needsCardholderInfo = isTapPay && selectedToken && !isCardholderInfoComplete(selectedToken.cardholder)

  const updateCardholderInfo = async (data: CardholderFormValues) => {
    if (!selectedTokenId) {
      throw new Error('找不到 token id')
    }
    await updatePaymentTokenInfo({
      country_code: countryCode,
      token_id: selectedTokenId,
      cardholder: {
        name_en: data.cardholderNameEn,
        email: data.email,
        phone_number: data.phoneNumber,
        phone_number_country_code: data.phoneCountryCode,
      },
    })
  }

  const updateOrderPaymentCard = async () => {
    if (!orderId || !projectCode || !userId || !selectedTokenId) {
      throw new Error('參數缺失')
    }
    await updateOrderPaymentToken({
      id: fortuneOrderId,
      project_code: projectCode,
      platform_user_id: userId,
      payment_token_id: selectedTokenId,
    })
  }

  const showSuccessSnackbar = () => {
    openSnackbar({
      severity: 'success',
      message: t('wpMyAccount.orderPaymentCardUpdated', {
        orderId,
        defaultValue: '訂單 #{{orderId}} 扣款卡號已更新',
      }),
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
    })
  }

  const showErrorSnackbar = () => {
    openSnackbar({
      severity: 'error',
      message: t('wpMyAccount.orderPaymentCardUpdateFailed', {
        orderId,
        defaultValue: '訂單 #{{orderId}} 扣款卡號更新失敗，未知原因',
      }),
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
    })
  }

  const executeUpdate = async (cardholderData?: CardholderFormValues) => {
    try {
      // 走表單驗證會由 handleSubmit 帶入 cardholderData，此時才呼叫 updateCardholderInfo
      if (cardholderData) {
        await updateCardholderInfo(cardholderData)
      }
      await updateOrderPaymentCard()
      mutateUserPaymentTokens()
      mutateAllECommerceOrders?.()
      closeDialog()
      showSuccessSnackbar()
    }
    catch (error) {
      showErrorSnackbar()
      console.error('Failed to update:', error)
    }
  }

  const handleConfirm = () => {
    // 若選擇的卡片是「缺少持卡人資訊」，需要走 form 驗證，並且更新持卡人資訊。而後才能更換 order 的信用卡
    if (needsCardholderInfo) {
      handleSubmit(executeUpdate)()
    }
    else {
      executeUpdate()
    }
  }

  return (
    <>
      <DialogHeader align="center" title={t('wpMyAccount.autoBilledCard', { defaultValue: '自動扣款卡號' })} onClose={closeDialog} />
      <DialogContent>
        {isLoading && (
          <VStack alignItems="center" py={4}>
            <CircularProgress color={colors.grey300} fontSize={20} />
          </VStack>
        )}
        <VStack gap={2}>
          {paymentTokens?.map((token) => {
            const isSelected = selectedTokenId === token.payment_token_id
            const showCardholderForm = isTapPay && isSelected && !isCardholderInfoComplete(token.cardholder)

            return (
              <PaymentTokenItem
                key={token.payment_token_id}
                token={token}
                isSelected={isSelected}
                isTapPay={isTapPay}
                onSelect={() => handleSelectToken(token.payment_token_id)}
              >
                {showCardholderForm && (
                  <VStack gap={2} pt={3} width="100%">
                    <Text>
                      {t('wpMyAccount.cardholderNameEn', { defaultValue: '持卡人英文姓名' })}
                    </Text>
                    <Controller
                      control={control}
                      name="cardholderNameEn"
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          onChange={e => field.onChange(filterCardholderName(e.target.value))}
                          placeholder="e.g., MEI HUA LIN"
                          error={!!error}
                          fullWidth
                          helpText={error?.message ? t(`wpMyAccount.${error.message}`, { defaultValue: error.message }) : undefined}
                        />
                      )}
                    />

                    <Text>
                      {t('wpMyAccount.cardholderEmail', { defaultValue: '持卡人 Email' })}
                    </Text>
                    <Controller
                      control={control}
                      name="email"
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          onChange={e => field.onChange(filterEmail(e.target.value))}
                          placeholder="name@example.com"
                          error={!!error}
                          fullWidth
                          helpText={error?.message ? t(`wpMyAccount.${error.message}`, { defaultValue: error.message }) : undefined}
                        />
                      )}
                    />

                    <Text>
                      {t('wpMyAccount.cardholderPhone', { defaultValue: '持卡人手機號碼' })}
                    </Text>
                    <VStack gap={1}>
                      <HStack gap={2}>
                        <Controller
                          control={control}
                          name="phoneCountryCode"
                          render={({ field, fieldState: { error } }) => (
                            <Input
                              {...field}
                              onChange={e => field.onChange(filterDigitsOnly(e.target.value))}
                              width="60px"
                              placeholder="886"
                              error={!!error}
                              maxLength={3}
                            />
                          )}
                        />
                        <Box flex={1}>
                          <Controller
                            control={control}
                            name="phoneNumber"
                            render={({ field }) => (
                              <Input
                                {...field}
                                onChange={e => field.onChange(filterDigitsOnly(e.target.value))}
                                type="tel"
                                placeholder="912345678"
                                fullWidth
                                error={!!errors.phoneCountryCode}
                              />
                            )}
                          />
                        </Box>
                      </HStack>
                      {errors.phoneCountryCode?.message && (
                        <Text variant="caption" color={colors.red600}>
                          {t(`wpMyAccount.${errors.phoneCountryCode.message}`, { defaultValue: errors.phoneCountryCode.message })}
                        </Text>
                      )}
                    </VStack>
                  </VStack>
                )}
              </PaymentTokenItem>
            )
          })}
        </VStack>
      </DialogContent>
      <DialogActions>
        <Button
          width="100%"
          variant="outlined"
          color="grey"
          onClick={closeDialog}
        >
          {t('wpMyAccount.cancel', { defaultValue: '取消' })}
        </Button>
        <Button
          width="100%"
          onClick={handleConfirm}
          loading={isPaymentTokenInfoUpdating || isOrderPaymentUpdating}
          disabled={isPaymentTokenInfoUpdating || isOrderPaymentUpdating || !selectedTokenId}
        >
          {t('wpMyAccount.confirm', { defaultValue: '確定' })}
        </Button>
      </DialogActions>
    </>
  )
}

export const DialogEditPaymentMethod = () => {
  const { open, closeDialog, orderId, paymentTokenId, projectCode, userId, fortuneOrderId } = useDialog<DialogEditPaymentMethodProps>(DIALOG_EDIT_PAYMENT_METHOD)

  return (
    <ResponsiveDialog onClose={closeDialog} open={open} maxWidth={320} fullWidth>
      <DialogEditPaymentMethodContent orderId={orderId} fortuneOrderId={fortuneOrderId} paymentTokenId={paymentTokenId} projectCode={projectCode} userId={userId} />
    </ResponsiveDialog>
  )
}
